import { useCallback, useMemo, useState } from 'react';
import { useStore } from '../../modules/store/cobrandStore';
import { StyledComparisonTray } from './CobrandComparisonTray.styles';
import { CompareCard } from './CompareCard';
import { isMobile, useLandscapeOrientation } from '../../modules/utils';
import { CobrandDrawer } from './CobrandDrawer';
import { useCheckBreakpoint, Button, Heading, tags, size, headingType } from '@marriott/mi-ui-library';
import { COMPARISON_TRAY_SLOTS, CardType } from '../../modules/constants';
import { CtaComponent } from '../../molecules/CtaComponent/CtaComponent';
import clsx from 'clsx';
import { CtaComponentTypes } from '../../molecules/CtaComponent/CtaComponent.types';
import { CobrandComparisonTrayProps } from './CobrandComparisonTray.types';

const toBoolean = (value?: boolean | string): boolean => value === true || value === 'true';

export const CobrandComparisonTray = (props: CobrandComparisonTrayProps) => {
  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');
  const isMobileDevice = isMobile();
  const isLandscape = useLandscapeOrientation();

  const enableComparisonTray = toBoolean(props?.isComparisonTrayEnabled);
  const { cobrandData, setCobrandData } = useStore(store => store);
  const [visible, setVisible] = useState(true);

  // Use row/desktop layout when:
  // 1. Device is in landscape orientation (mobile or tablet), OR
  // 2. Desktop viewport (>= 992px) and not a mobile device
  // This allows mobile landscape to render the row layout
  const useDesktopLayout = isLandscape || isViewportM;

  // Mobile device in landscape - used to hide certain UI elements
  const isMobileLandscape = isMobileDevice && isLandscape;

  // Memoize slots for performance and clarity
  const slots = useMemo(
    () => (isViewportL && !isMobileDevice ? COMPARISON_TRAY_SLOTS.DESKTOP : COMPARISON_TRAY_SLOTS.MOBILE),
    [isViewportL, isMobileDevice]
  );

  const selectedCardCount = cobrandData?.selectedCards?.length ?? 0;
  const cardsSelectedText = useMemo(
    () => `Cards Selected: ${selectedCardCount} of ${slots.length}`,
    [selectedCardCount, slots.length]
  );

  // Extract CTA props from model
  const ctaProps = useMemo(() => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const cqItems = props?.cqItems ?? (props?.model?.cqItems as Record<string, any>);
    const pageItems = props?.isFromWrapper
      ? cqItems?.['variable-column-0']?.[':items'] ?? {}
      : cqItems?.['page-0']?.[':items'] ?? {};

    for (const itemKey in pageItems) {
      if (itemKey.includes('cta_component')) {
        return pageItems[itemKey] as CtaComponentTypes;
      }
    }
    return undefined;
  }, [props?.cqItems, props?.isFromWrapper, props?.model?.cqItems]);

  const resolvedCtaProps = useMemo(() => {
    if (!ctaProps) return undefined;
    return { ...ctaProps };
  }, [ctaProps]);

  const handleClearAll = useCallback(() => {
    setCobrandData({ ...cobrandData, selectedCards: [] });
  }, [cobrandData, setCobrandData]);

  const handleDrawerChange = useCallback(() => {
    setVisible(v => !v);
  }, []);

  // Hide component if no CTA props
  if (!resolvedCtaProps) return null;

  // Hide if no selected cards or feature disabled
  if (!selectedCardCount || !enableComparisonTray) {
    return null;
  }

  return (
    <CobrandDrawer
      variant="drawer"
      placement="bottom"
      initialState={visible}
      title={
        <StyledComparisonTray isCollapsed>
          <div className="container py-2 d-flex flex-row justify-content-between align-items-center">
            <div>
              <Heading
                titleText={cardsSelectedText}
                fontSize={size.large}
                element={tags.h5}
                variation={headingType.subtitle}
              />
            </div>

            <div className="d-flex flex-row align-items-center">
              {!isMobileLandscape && (
                <Button
                  isLink={true}
                  buttonCopy="Clear all"
                  callback={handleClearAll}
                  className={clsx('clear-btn ', useDesktopLayout ? 'd-block mr-2' : 'd-none')}
                />
              )}
              <div className="ml-1">
                <CtaComponent
                  {...resolvedCtaProps}
                  comparisonTrayFlag={true}
                  isComparisonTrayEnabled={enableComparisonTray}
                  hideCtaDescription={isMobileLandscape}
                />
              </div>
            </div>
          </div>
        </StyledComparisonTray>
      }
      onChange={handleDrawerChange}
    >
      <StyledComparisonTray
        data-testid="cobrand-CobrandComparisonTray"
        data-component-name="o-cobrand-CobrandComparisonTray"
      >
        <div className="container pt-4">
          <Heading
            titleText={cardsSelectedText}
            fontSize={size.large}
            element={tags.h5}
            variation={headingType.subtitle}
          />
          <div className={clsx('compare-cards d-flex mt-3', useDesktopLayout ? 'flex-row' : 'flex-column')}>
            <div className={clsx('d-flex', useDesktopLayout ? 'flex-row' : 'flex-column')}>
              {slots.map(index => {
                const cardItem = cobrandData.selectedCards[index];
                return (
                  <CompareCard
                    key={index}
                    index={index}
                    item={cardItem}
                    defaultContent={!cardItem}
                    styleClass={clsx(useDesktopLayout ? 'mr-2 mb-0' : 'mb-3')}
                    hideShellSubtitle={isMobileLandscape}
                    data-testid={`compare-card-${index}`}
                  />
                );
              })}
            </div>
            <div
              className={clsx(
                'tray-actions',
                useDesktopLayout ? 'd-flex justify-content-end align-items-center' : 'cta-wrapper'
              )}
            >
              {!isMobileLandscape && (
                <Button
                  isLink
                  buttonCopy="Clear all"
                  callback={handleClearAll}
                  className={clsx('clear-btn', !isMobileDevice ? 'd-block mr-4' : 'd-none')}
                />
              )}
              <CtaComponent
                {...resolvedCtaProps}
                comparisonTrayFlag={true}
                isComparisonTrayEnabled={enableComparisonTray}
                hideCtaDescription={isMobileLandscape}
                removeTopMargin={true}
              />
            </div>
          </div>
        </div>
      </StyledComparisonTray>
    </CobrandDrawer>
  );
};
